import { useRouter } from 'next/router'
import { FormEvent, Fragment, useEffect, useRef, useState } from 'react'

import { useQuery } from '@tanstack/react-query'
import clsx from 'clsx'
import { FormattedMessage, useIntl } from 'react-intl'

import { Combobox, Transition } from '@headlessui/react'

import { findArticles } from '@/lib/api/other'
import { Article } from '@/lib/types/articles'

import Icon from '@/components/Icon'
import Input from '@/components/Input'
import Loader from '@/components/Loader'
import Overlay from '@/components/Overlay'

export const ArticleSelect = ({
  className,
  hasOutline = true,
  buttonClassName
}: {
  hasOutline?: boolean
  className?: string
  buttonClassName?: string
}) => {
  const router = useRouter()
  const locale = router.locale

  const { formatMessage } = useIntl()

  const buttonRef = useRef<HTMLButtonElement>(null)

  const [query, setQuery] = useState('')
  const [article, setArticle] = useState<Article | null>(null)
  const [previousData, setPreviousData] = useState<Article[] | null>(null)

  const { data: articles, isFetching } = useQuery<Article[]>({
    queryKey: ['articles', router.query.id, locale, query],
    queryFn: () => findArticles(query),
    enabled: router.isReady
  })

  useEffect(() => {
    if (articles) {
      setPreviousData(articles)
    }
  }, [articles])

  const onInputChange = (event: FormEvent<HTMLInputElement>) => {
    const target = event.target as HTMLInputElement
    setQuery(target.value)
  }

  const deleteSearchValue = () => {
    setQuery('')
    setArticle(null)
  }

  useEffect(() => {
    if (article) {
      const { id, slug } = article

      router.push({
        pathname: '/support/[id]/[slug]',
        query: { id, slug }
      })

      setArticle(null)
    }
  }, [article, router])

  const currentArticles = isFetching ? previousData : articles

  return (
    <Combobox onChange={setArticle} value={article}>
      {({ open }) => (
        <div
          className={clsx(
            'relative flex w-full items-center border-slate-300 font-medium transition-outline',
            'rounded-xl border border-slate-300 py-2.5 duration-500 desktop:pl-4 desktop:text-zink-800',
            open && hasOutline
              ? '!outline !outline-1 !outline-primary-600'
              : '!outline-slate-300',
            className
          )}
        >
          <Combobox.Button
            className={clsx(
              'flex w-full desktop:items-center',
              buttonClassName
            )}
            ref={buttonRef}
          >
            <Combobox.Input
              className={clsx(
                'w-10/12 truncate placeholder:text-secondary-500',
                open && 'max-desktop:opacity-0'
              )}
              onChange={(event) => setQuery(event.target.value)}
              spellCheck={false}
              placeholder={formatMessage({
                id: 'support.articleSearch',
                defaultMessage: 'Поиск статьи'
              })}
            />
            {isFetching && open ? (
              <Loader className="absolute right-4" size="h-5 w-5" />
            ) : (
              (!!query || !!article) && (
                <div
                  className="absolute right-4 hidden desktop:block"
                  onClick={deleteSearchValue}
                >
                  <Icon className="h-4 w-4" name="close-16" />
                </div>
              )
            )}
          </Combobox.Button>
          {(!!articles?.length || query) && (
            <Transition
              afterLeave={() => setQuery('')}
              as={Fragment}
              enter="duration-200 ease-out"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="duration-200 ease-in"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Combobox.Options
                className={clsx(
                  'absolute left-0 right-0 top-16 w-full rounded-xl border-slate-300 desktop:z-10',
                  'bg-white desktop:border desktop:py-4 desktop:shadow-md'
                )}
              >
                <ArticleOptionsList
                  className="max-desktop:hidden"
                  {...{ articles: currentArticles || [], query }}
                />
                <Overlay
                  className="z-50"
                  backButtonRef={buttonRef}
                  title={
                    <Input
                      className="w-full text-base font-normal text-zink-800"
                      autoFocus={true}
                      defaultValue={article ? article.title : query}
                      onChange={(event) => onInputChange(event)}
                      placeholder={formatMessage({
                        id: 'support.articleSearch',
                        defaultMessage: 'Поиск статьи'
                      })}
                    />
                  }
                >
                  <ArticleOptionsList
                    className="h-[87vh] w-full self-start"
                    {...{ articles: currentArticles || [], query }}
                  />
                </Overlay>
              </Combobox.Options>
            </Transition>
          )}
        </div>
      )}
    </Combobox>
  )
}

const ArticleOption = ({ article }: { article: Article }) => {
  return (
    <Combobox.Option key={article.id} value={article}>
      <div
        className={clsx(
          'flex w-full cursor-pointer items-center rounded-xl px-3',
          'py-2 text-left text-zinc-800 transition-colors desktop:hover:bg-slate-100'
        )}
      >
        <span className="mr-3 flex h-11 w-11 flex-none items-center justify-center rounded-full bg-slate-100">
          <Icon
            className="h-6 w-6 flex-none text-secondary-500"
            name="document-fill-24"
          />
        </span>
        <span
          className={clsx(
            'overflow-hidden overflow-ellipsis whitespace-nowrap text-x-sm',
            'leading-x-sm tablet:text-x-base tablet:leading-x-base'
          )}
        >
          {article.title}
        </span>
      </div>
    </Combobox.Option>
  )
}

const ArticleOptionsList = ({
  articles,
  query,
  className
}: {
  articles: Article[]
  query: string
  className?: string
}) => {
  const isEmptyList = articles.length === 0 && query !== ''

  return (
    <div
      className={clsx(
        'scroll-wrap overflow-y-auto desktop:mr-1 desktop:max-h-[33vh]',
        className
      )}
    >
      {isEmptyList ? (
        <div
          className={clsx(
            'relative mx-auto flex cursor-default select-none',
            'flex-col px-4 py-4 text-center text-gray-700'
          )}
        >
          <p className="text-x-base leading-x-base">
            <FormattedMessage
              {...{
                id: 'support.article.notFound',
                defaultMessage: 'Статья не найдена'
              }}
            />
          </p>
        </div>
      ) : (
        articles.map((article, index) => (
          <ArticleOption {...{ article }} key={index} />
        ))
      )}
    </div>
  )
}
